import { useEffect, useState } from 'react'
import { SIGNED_OUT_EVENT, api } from './lib/api'
import {
  SIGN_IN_PATH,
  allowedPath,
  discoveryRoute,
  homePath,
  isSignedInPath,
  linkToken,
  principalToken,
  rememberPath,
  spacePath,
  takeRememberedPath,
  verifyToken,
} from './lib/paths'
import AppShell from './pages/AppShell'
import CoverPage from './pages/CoverPage'
import LoginPage from './pages/LoginPage'
import PrincipalLinkPage from './pages/PrincipalLinkPage'
import ResendLinkPage from './pages/ResendLinkPage'
import SessionPage from './pages/SessionPage'
import VerifyPage from './pages/VerifyPage'

// Path switching BETWEEN surfaces (no router dep; every shape lives in
// lib/paths.js):
//   /s/<token>            — the public link page; never behind auth
//   /verify/<token>       — magic sign-in landing page. Its own path, outside
//                           /admin, because it is not a role's home
//   /p/<token>            — the principal's personal link: exchanged for a
//                           session on load, then straight into their space.
//                           A dead link shows the resend card
//   /admin…, /discovery…  — the signed-in app: /me decides. Signed out, /admin…
//                           shows the login card; a space URL shows the card
//                           that resends the principal's personal link.
//                           Signed in, AppShell routes within the app and lands
//                           admins on /admin, principals on /discovery
//   everything else       — CoverPage, including /. Not a redirect, and no
//                           link to /admin from it: that path is deliberately
//                           unlinked.
export default function App() {
  const [pathname] = useState(() => window.location.pathname)
  const token = linkToken(pathname)
  const verifyTok = verifyToken(pathname)
  const pToken = principalToken(pathname)
  // A completed sign-in continues straight into the app, so the verify page
  // and the personal link count as signed-in surfaces even though their paths
  // are not under one.
  const signedIn = isSignedInPath(pathname) || verifyTok !== null || pToken !== null
  const [user, setUser] = useState(null)
  // No cookie exists until the verify page's POST or the personal link's
  // exchange succeeds, so /me would only 401 there: nothing to check.
  const [checking, setChecking] = useState(signedIn && !verifyTok && !pToken)
  // Where AppShell mounts: the URL at load, unless the role may not see it.
  const [landing, setLanding] = useState(pathname)
  // A spent verify link offers "request a new one" — the login card, in place.
  const [showLogin, setShowLogin] = useState(false)
  // The session lapsed while they were in the app (api.js saw a 401): the
  // signed-out card says so instead of pretending they never signed in.
  const [expired, setExpired] = useState(false)

  useEffect(() => {
    function handleSignedOut() {
      setUser((current) => {
        if (current) setExpired(true)
        return null
      })
    }
    window.addEventListener(SIGNED_OUT_EVENT, handleSignedOut)
    return () => window.removeEventListener(SIGNED_OUT_EVENT, handleSignedOut)
  }, [])

  useEffect(() => {
    if (!signedIn || verifyTok || pToken) return undefined
    let cancelled = false
    api('/api/v1/auth/me')
      .then((me) => {
        if (cancelled) return
        const dest = allowedPath(pathname, me.role) ? pathname : homePath(me.role)
        if (dest !== pathname) window.history.replaceState(null, '', dest)
        setLanding(dest)
        setUser(me)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [signedIn, verifyTok, pToken, pathname])

  useEffect(() => {
    // About to render a signed-out card: remember where they were headed so the
    // sign-in round-trip lands there, not on the bare home. Covers both
    // arriving signed-out on a result link and logging out while reading one.
    if (!signedIn || verifyTok || pToken || checking || user) return
    rememberPath(window.location.pathname)
  }, [signedIn, verifyTok, pToken, checking, user])

  async function handleLogout() {
    try {
      await api('/api/v1/auth/logout', { method: 'POST' })
    } catch {
      // Cookie may already be gone; treat as logged out either way.
    }
    setUser(null)
  }

  function land(me, dest) {
    window.history.replaceState(null, '', dest)
    setLanding(dest)
    setChecking(false)
    setExpired(false)
    setUser(me)
  }

  function handleVerified(me) {
    // The single-use token is spent — drop it from the address bar so a
    // bookmark/refresh lands in the app, not on a dead verify page. A
    // remembered destination wins when this role may see it; otherwise the
    // role's home. Consumed either way, so it cannot redirect a later sign-in.
    land(me, takeRememberedPath(me.role) ?? homePath(me.role))
  }

  function handlePrincipalSignedIn(me) {
    // The link opens ONE space, so that is where it lands — a remembered
    // destination is not theirs and is consumed so it cannot redirect a later
    // sign-in. The token stays in the inbox, not in the address bar.
    takeRememberedPath(me.role)
    land(me, spacePath(me.space_id))
  }

  function handleRequestNewLink() {
    window.history.replaceState(null, '', SIGN_IN_PATH)
    setShowLogin(true)
  }

  if (token) {
    return <SessionPage token={token} />
  }

  if (verifyTok && !user && !showLogin) {
    return (
      <VerifyPage token={verifyTok} onVerified={handleVerified} onRequestNewLink={handleRequestNewLink} />
    )
  }

  if (pToken && !user) {
    return <PrincipalLinkPage token={pToken} onSignedIn={handlePrincipalSignedIn} />
  }

  if (!signedIn) {
    return <CoverPage />
  }

  if (checking) {
    return (
      <main
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--color-surface-alt)' }}
      >
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }} role="status">
          Loading…
        </p>
      </main>
    )
  }

  if (!user) {
    // Read live: a sign-out from inside the shell leaves the URL wherever the
    // shell had moved it.
    const space = discoveryRoute(window.location.pathname)
    const notice = expired
      ? 'Your session has ended. Enter your email and we’ll send your personal link again.'
      : null
    return space?.spaceId ? <ResendLinkPage notice={notice} /> : <LoginPage />
  }

  return <AppShell key={landing} user={user} onLogout={handleLogout} initialPath={landing} />
}
